import React, { useState } from 'react';

interface FriendRequestItem {
  id: string;
  user: {
    name: string;
    avatar: string;
    level: number;
    mutualFriends: number;
  };
  timestamp: string;
}

interface FriendRequestProps {
  onAccept?: (requestId: string) => void;
  onReject?: (requestId: string) => void;
}

export const FriendRequest: React.FC<FriendRequestProps> = ({
  onAccept,
  onReject
}) => {
  const [requests, setRequests] = useState<FriendRequestItem[]>([
    {
      id: '1',
      user: {
        name: 'Pedro Lima',
        avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20avatar%20portrait%20simple%20background%20friendly%20smile%20modern&width=100&height=100&seq=request-001&orientation=squarish',
        level: 14,
        mutualFriends: 3
      },
      timestamp: 'Há 2 horas'
    },
    {
      id: '2',
      user: {
        name: 'Fernanda Souza',
        avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20avatar%20portrait%20simple%20background%20warm%20smile%20elegant&width=100&height=100&seq=request-002&orientation=squarish',
        level: 16,
        mutualFriends: 5
      },
      timestamp: 'Há 1 dia'
    },
    {
      id: '3',
      user: {
        name: 'Ricardo Mendes',
        avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20avatar%20portrait%20simple%20background%20confident%20professional&width=100&height=100&seq=request-003&orientation=squarish',
        level: 12,
        mutualFriends: 2
      },
      timestamp: 'Há 2 dias'
    }
  ]);

  const handleAccept = (requestId: string) => {
    setRequests((prev) => prev.filter((r) => r.id !== requestId));
    onAccept?.(requestId);
  };

  const handleReject = (requestId: string) => {
    setRequests((prev) => prev.filter((r) => r.id !== requestId));
    onReject?.(requestId);
  };

  if (requests.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
        <i className="ri-user-add-line text-6xl text-gray-300 mb-4"></i>
        <h3 className="text-xl font-bold text-gray-800 mb-2">
          Nenhuma solicitação pendente
        </h3>
        <p className="text-gray-600">
          Você não tem solicitações de amizade no momento.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-user-add-line text-2xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold">Solicitações de Amizade</h2>
            <p className="text-white/80">
              {requests.length} {requests.length === 1 ? 'solicitação pendente' : 'solicitações pendentes'}
            </p>
          </div>
        </div>
      </div>

      {/* Lista de Solicitações */}
      <div className="grid gap-4">
        {requests.map((request) => (
          <div
            key={request.id}
            className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all"
          >
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <img
                  src={request.user.avatar}
                  alt={request.user.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-teal-500"
                />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-xs font-bold border-2 border-white">
                  {request.user.level}
                </div>
              </div>

              {/* Informações */}
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-800 text-lg mb-1">
                  {request.user.name}
                </h3>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span>
                    <i className="ri-vip-crown-line text-teal-500 mr-1"></i>
                    Nível {request.user.level}
                  </span>
                  <span>
                    <i className="ri-group-line text-purple-500 mr-1"></i>
                    {request.user.mutualFriends} amigos em comum
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{request.timestamp}</p>
              </div>

              {/* Ações */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleAccept(request.id)}
                  className="px-6 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-600 transition-all whitespace-nowrap"
                >
                  <i className="ri-check-line mr-2"></i>
                  Aceitar
                </button>
                <button
                  onClick={() => handleReject(request.id)}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all whitespace-nowrap"
                >
                  <i className="ri-close-line mr-2"></i>
                  Recusar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
